import {
  BetSlipMode,
  BonusCategory,
  BonusStatus,
  BonusType,
  WinBoostEligibilityCondition,
} from "../../types/enums";

export const getBonusAmount = (bonus: IBetSlipBonus): number => {
  const userBonusData = bonus.userBonus?.[0] ?? null;
  return (
    bonus.FbFixedBonusAmount ||
    userBonusData?.Reward ||
    bonus.maxBonus ||
    bonus.Reward ||
    0
  );
};

export const mapBonus = (bonus: IBetSlipBonus): MappedBonus => {
  const amount = getBonusAmount(bonus);
  return {
    id: bonus.id.toString(),
    val: bonus.name,
    label: bonus.description || bonus.name,
    validTo: bonus.ValidTo,
    amount,
    percentage: bonus.bonusPercent || 0,
    disabled: false,
    originalBonus: bonus,
  };
};

export const selectActiveBonuses = (
  rawBonuses: IBetSlipBonus[],
  staticBonuses: IBetSlipBonus[],
  userCurrencyId: number,
): {
  freeBets: MappedBonus[];
  refundBonus: MappedBonus | null;
  winBoosts: MappedBonus[];
} => {
  const now = new Date();
  const freeBetsList: MappedBonus[] = [];
  const refundBonusesList: MappedBonus[] = [];
  const winBoostsList: MappedBonus[] = [];

  [...rawBonuses, ...staticBonuses].forEach((bonus) => {
    if (bonus.currencyId && bonus.currencyId !== userCurrencyId) return;
    if (!isBonusActive(bonus, now)) return;
    const mapped = mapBonus(bonus);
    switch (identifyBonusType(bonus)) {
      case BonusCategory.FREE_BET:
        freeBetsList.push(mapped);
        break;
      case BonusCategory.REFUND_BONUS:
        refundBonusesList.push(mapped);
        break;
      case BonusCategory.WIN_BOOST:
        winBoostsList.push(mapped);
        break;
    }
  });

  return {
    freeBets: freeBetsList,
    refundBonus: selectBestBonus(refundBonusesList, BonusCategory.REFUND_BONUS),
    winBoosts: [...winBoostsList].sort(
      (a, b) =>
        (a.originalBonus.minSelection || 0) -
        (b.originalBonus.minSelection || 0),
    ),
  };
};

/**
 * Identify bonus type based on properties
 * Uses both bonusTypeId and property-based detection for robustness
 */
export const identifyBonusType = (bonus: IBetSlipBonus): BonusCategory => {
  // Primary check: bonusTypeId
  if (bonus.bonusTypeId === BonusType.FreeBet) return BonusCategory.FREE_BET;
  if (bonus.bonusTypeId === BonusType.RefundBonus)
    return BonusCategory.REFUND_BONUS;
  if (bonus.bonusTypeId === BonusType.WinBoost) return BonusCategory.WIN_BOOST;

  // Fallback: property-based detection
  if (bonus.FbFixedBonusAmount > 0 || bonus.Reward)
    return BonusCategory.FREE_BET;
  if (bonus.refundTimes !== null && bonus.refundTimes > 0)
    return BonusCategory.REFUND_BONUS;
  if (bonus.bonusPercent !== null && bonus.bonusPercent > 0)
    return BonusCategory.WIN_BOOST;

  return BonusCategory.UNKNOWN;
};

/**
 * Check if bonus is currently active based on multiple criteria
 */
export const isBonusActive = (bonus: IBetSlipBonus, now: Date): boolean => {
  // Identify bonus type first
  const bonusType = identifyBonusType(bonus);

  // Check isActive flag and isDeleted flag
  if (!bonus.isActive || bonus.isDeleted) {
    return false;
  }

  // Check date range
  const validFrom = new Date(bonus.validFrom);
  const validTo = new Date(bonus.ValidTo);
  if (!(now >= validFrom && now <= validTo)) {
    return false;
  }

  // Additional type-specific checks
  switch (bonusType) {
    case BonusCategory.FREE_BET:
      // Check if userBonus exists and has the required properties
      if (bonus.userBonus && bonus.userBonus.length > 0) {
        const userBonusData = bonus.userBonus[0];

        // Check status (if present in userBonus)
        if (
          !userBonusData.status ||
          userBonusData.status !== BonusStatus.Active
        ) {
          return false;
        }

        // Check expiration date (if present in userBonus)
        if (userBonusData.bonusExpirationDate) {
          const expirationDate = new Date(userBonusData.bonusExpirationDate);
          if (now > expirationDate) {
            return false;
          }
        }

        // Check if has positive amount or reward
        return (
          bonus.FbFixedBonusAmount > 0 ||
          bonus.maxBonus > 0 ||
          Boolean(userBonusData.Reward && userBonusData.Reward > 0)
        );
      } else {
        // Fallback to legacy properties for bonuses without userBonus array
        const hasValidStatus =
          bonus.status === undefined || bonus.status === BonusStatus.Active;
        const hasValidExpiration =
          bonus.bonusExpirationDate === undefined ||
          now <= new Date(bonus.bonusExpirationDate);

        return (
          hasValidStatus &&
          hasValidExpiration &&
          (bonus.FbFixedBonusAmount > 0 ||
            bonus.maxBonus > 0 ||
            Boolean(bonus.Reward && bonus.Reward > 0))
        );
      }

    case BonusCategory.REFUND_BONUS:
      // Refund bonuses must have refund times available
      return bonus.refundTimes !== null && bonus.refundTimes > 0;

    case BonusCategory.WIN_BOOST:
      // Win boosts must have a positive bonus percentage
      return bonus.bonusPercent !== null && bonus.bonusPercent > 0;

    default:
      return false;
  }
};

/**
 * Select the best bonus from a list based on type-specific criteria
 */
export const selectBestBonus = (
  bonuses: MappedBonus[],
  bonusType: BonusCategory,
): MappedBonus | null => {
  if (bonuses.length === 0) return null;
  if (bonuses.length === 1) return bonuses[0];

  return bonuses.reduce((prev, curr) => {
    const prevBonus = prev.originalBonus;
    const currBonus = curr.originalBonus;

    // For REFUND bonuses: Pick highest refundTimes
    if (bonusType === BonusCategory.REFUND_BONUS) {
      const prevRefundTimes = prevBonus.refundTimes || 0;
      const currRefundTimes = currBonus.refundTimes || 0;
      if (prevRefundTimes !== currRefundTimes) {
        return currRefundTimes > prevRefundTimes ? curr : prev;
      }
    }

    // Check branch priority (branch-specific bonuses take priority)
    const prevBranch =
      prevBonus.branchId !== null &&
      prevBonus.branchId !== -1 &&
      prevBonus.branchId !== 0
        ? 1
        : 0;
    const currBranch =
      currBonus.branchId !== null &&
      currBonus.branchId !== -1 &&
      currBonus.branchId !== 0
        ? 1
        : 0;

    if (prevBranch !== currBranch) {
      return currBranch > prevBranch ? curr : prev;
    }

    // For WIN BOOST bonuses: Pick highest bonusPercent
    if (bonusType === BonusCategory.WIN_BOOST) {
      const prevPercent = prevBonus.bonusPercent || 0;
      const currPercent = currBonus.bonusPercent || 0;
      return currPercent > prevPercent ? curr : prev;
    }

    return prev;
  });
};

export const resolveWinBoostCondition = (
  activeWinBoost: MappedBonus | null,
  isFreeBetActive: boolean,
  totalStake: number,
  totalOdds: number,
  selections: IBetSlipSelection[],
  betBuilderOdds: Array<{ odd: number }>,
  stakes: { [key: string]: number },
  mode: BetSlipMode,
): WinBoostEligibilityCondition => {
  if (!activeWinBoost) return WinBoostEligibilityCondition.NoActiveBoost;
  if (isFreeBetActive) return WinBoostEligibilityCondition.FreeBetActive;

  const bonus = activeWinBoost.originalBonus;

  if (totalOdds < bonus.minTotalOdds) return WinBoostEligibilityCondition.InsufficientTotalOdds;

  if (bonus.minOddPerRow > 1) {
    const hasLowOdds =
      selections.some((s) => s.oddPrice < bonus.minOddPerRow) ||
      betBuilderOdds.some((b) => b.odd < bonus.minOddPerRow);
    if (hasLowOdds) return WinBoostEligibilityCondition.InsufficientMinOddPerRow;
  }

  if (bonus.minStakeAmount > 0) {
    if (mode === BetSlipMode.Combo && totalStake < bonus.minStakeAmount)
      return WinBoostEligibilityCondition.InsufficientMinStakeCombo;
    if (mode === BetSlipMode.Single) {
      const allBelowMin = selections.every((s) => (stakes[s.key] || 0) < bonus.minStakeAmount);
      if (allBelowMin) return WinBoostEligibilityCondition.InsufficientMinStakeSingle;
    }
  }

  return WinBoostEligibilityCondition.Eligible;
};

export interface FreeBetConditions {
  meetsMinOdds: boolean;
  meetsMinSelections: boolean;
  isEligible: boolean;
}

/**
 * Single mode has no combined "total odds" across independent selections -
 * a min-odds requirement can only be measured (and met) there when exactly
 * one selection is in the slip, using that selection's own price. In combo
 * mode the real combined odds (comboTotalOdds) apply regardless of count.
 */
export const resolveFreeBetConditions = (
  bonus: IBetSlipBonus,
  mode: BetSlipMode,
  totalSelections: number,
  comboTotalOdds: number,
  singleSelectionOddPrice?: number,
): FreeBetConditions => {
  const minOddsThreshold = bonus.minTotalOdds || bonus.minOddPerRow || 0;
  const meetsMinSelections = totalSelections >= (bonus.minSelection || 1);

  let meetsMinOdds: boolean;
  if (minOddsThreshold === 0) {
    meetsMinOdds = true;
  } else if (mode === BetSlipMode.Combo) {
    meetsMinOdds = comboTotalOdds >= minOddsThreshold;
  } else {
    meetsMinOdds = totalSelections === 1 && (singleSelectionOddPrice ?? 0) >= minOddsThreshold;
  }

  return { meetsMinOdds, meetsMinSelections, isEligible: meetsMinOdds && meetsMinSelections };
};

const buildCongratsMessage = (
  activeBoost: MappedBonus,
  nextBoost: MappedBonus,
  totalSelections: number,
  t: (key: keyof TranslationKeys, params?: Record<string, string | number>) => string,
): string => {
  const needed = (nextBoost.originalBonus.minSelection || 0) - totalSelections;
  const congratsKey =
    activeBoost.originalBonus.minSelection !== 1
      ? "SeoBonusDescription10Plural"
      : "SeoBonusDescription10Singular";
  const congrats = t(congratsKey, {
    "bonus.minSelection": activeBoost.originalBonus.minSelection,
    "bonus.bonusPercent": activeBoost.percentage,
  });
  const higher = t("SeoBonusTemplateTeasersNextBonusHigher", {
    "bonuses.nextBonus.selectionToAdd": needed,
    "bonuses.nextBonus.minOddPerRow": nextBoost.originalBonus.minOddPerRow,
    "bonuses.nextBonus.bonusPercent": nextBoost.percentage,
  });
  return `${congrats} ${higher}`;
};

const buildTeaserMessage = (
  nextBoost: MappedBonus,
  totalSelections: number,
  t: (key: keyof TranslationKeys, params?: Record<string, string | number>) => string,
): string => {
  const needed = (nextBoost.originalBonus.minSelection || 0) - totalSelections;
  const teaserKey =
    needed !== 1
      ? "SeoBonusTemplateTeasersNextBonusTipPlural"
      : "SeoBonusTemplateTeasersNextBonusTipSingular";
  return t(teaserKey, {
    "bonuses.nextBonus.selectionToAdd": needed,
    "bonuses.nextBonus.minOddPerRow": nextBoost.originalBonus.minOddPerRow,
    "bonuses.nextBonus.bonusPercent": nextBoost.percentage,
  });
};

const buildCongratsKey = (minSelection: number): keyof TranslationKeys =>
  minSelection !== 1 ? "SeoBonusDescription10Plural" : "SeoBonusDescription10Singular";

// Operators may still have the legacy Angular conditional template syntax in their
// translationKeys config. Extract the plain fallback text (last pipe-separated segment).
const stripAngularTemplate = (raw: string): string => {
  if (raw.startsWith("{") && raw.includes(":if")) {
    const lastPipe = raw.lastIndexOf("|");
    if (lastPipe > 0) return raw.slice(lastPipe + 1).replace(/\}$/, "");
  }
  return raw;
};

export const resolveWinBoostMessage = (
  condition: WinBoostEligibilityCondition,
  activeBoost: MappedBonus | null,
  nextBoost: MappedBonus | null,
  totalSelections: number,
  t: (key: keyof TranslationKeys, params?: Record<string, string | number>) => string,
): string | null => {
  switch (condition) {
    case WinBoostEligibilityCondition.Eligible: {
      if (!activeBoost) return null;
      if (nextBoost) return buildCongratsMessage(activeBoost, nextBoost, totalSelections, t);
      return t(buildCongratsKey(activeBoost.originalBonus.minSelection), {
        "bonus.minSelection": activeBoost.originalBonus.minSelection,
        "bonus.bonusPercent": activeBoost.percentage,
      });
    }
    case WinBoostEligibilityCondition.InsufficientMinStakeSingle:
    case WinBoostEligibilityCondition.InsufficientTotalOdds:
    case WinBoostEligibilityCondition.InsufficientMinOddPerRow:
      return stripAngularTemplate(t("SeoBonusTemplate0StakeError"));
    case WinBoostEligibilityCondition.InsufficientMinStakeCombo:
      return activeBoost
        ? t("SeoBonusTemplateContentStakeError", {
            "bonus.nextBonus.minStakeAmount": activeBoost.originalBonus.minStakeAmount,
          })
        : null;
    case WinBoostEligibilityCondition.NoActiveBoost:
      return nextBoost ? buildTeaserMessage(nextBoost, totalSelections, t) : null;
    default:
      return null;
  }
};
